<template lang="pug">
ValidationProvider(
  ref="maskValidator"
  :rules="validationRules"
  :vid="id"
  immediate
  v-slot="{ errors }"
  tag="div"
)
  div(
    :class="[{ 'error-styles': errors[0] }, { 'disabled-field': disabled },'lf-mask-container']"
  )
    div(v-if="label" class="label-container")
      div(class="label-text") {{ label }}
      span(
        v-if="requiredField"
        class="required-field") *
      v-tooltip(
          v-if="showInfo"
          top
          color="primary"
          dark
        )
        template(v-slot:activator="{ on, attrs }")
          span(v-bind="attrs" v-on="on" :class="[icons.info, 'label-icon']")
        span(class="show-info-message") {{ showInfo }}
    ejs-maskedtextbox(
      ref="maskTextBox"
      :id="id"
      :name="name"
      :showClearButton="true"
      :mask="mask"
      v-model="innerValue"
      @blur="emitValueOnBlur"
      :class="[{ 'error-styles': errors[0] }, 'lf-mask-input']"
      :created="onTextBoxCreated"
      :destroyed="onTextBoxDestroyed"
      )

    div(class="messages-container")
      span(v-if="errors.length" class="alert-message") {{ errors[0] }}
      span(v-else class="help-message") {{ helpText }}

</template>

<script lang="ts">
import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator'
import { MaskedTextBoxPlugin } from '@syncfusion/ej2-vue-inputs'
import { ValidationProvider } from 'vee-validate'
import { Icons } from '@/icons/icons'
import MaskTextBoxClearButtonMixin from '@/mixins/MaskTextBoxClearButtonMixin.vue'
import { mixins } from 'vue-class-component'

Vue.use(MaskedTextBoxPlugin)

@Component({
  components: {
    ValidationProvider
  }
})
export default class LexonMaskComponent extends mixins(MaskTextBoxClearButtonMixin) {
  @Prop({
    type: String
  })
  validationRules!: string

  @Prop({
    type: Boolean,
    default: false
  })
  disabled!: boolean

  @Prop({
    type: Boolean,
    default: false
  })
  showInfo!: boolean

  @Prop({
    type: [Number, String]
  })
  value!: number

  @Prop({
    type: String
  })
  id!: string

  @Prop({
    type: String
  })
  name!: string

  @Prop({
    type: String
  })
  helpText!: string

  @Prop({
    type: String
  })
  label!: string

  @Prop({
    type: String
  })
  placeholder!: string

  @Prop({
    type: String
  })
  validatorName!: string

  @Prop({
    type: String,
    required: true
  })
  mask!: string

  @Prop({
    type: Number,
    default: null
  })
  defaultValue!: number | null

  $refs!: {
    maskValidator: InstanceType<typeof ValidationProvider>
  }

  icons = {
    info: Icons.INFO_FULL
  }

  innerValue: any = this.defaultValue

  @Watch('innerValue')
  changedInnerValue(newVal: string) {
    this.validateField()
    this.emitValue(newVal)
  }

  @Watch('value')
  changedValue(value: string) {
    this.innerValue = value
  }

  @Emit()
  emitValue(value: any) {
    return this.$emit('input', value)
  }

  @Emit()
  emitValueOnBlur(event: any) {
    if (event.value) {
      return this.$emit('blur', event.value)
    }
  }

  get requiredField() {
    return !!(this.validationRules && this.validationRules.includes('required'))
  }

  validateField() {
    if (this.$refs.maskValidator) {
      return this.$refs.maskValidator.validate()
    }
  }

  created() {
    if (this.value) {
      this.innerValue = this.value
    }
  }
}
</script>

<style lang="scss" scoped>
.lf-mask-container {
  @include flex($flex-direction: column, $align-items: flex-start);
  @include textfield-messages;
  width: 100%;

  .label-container {
    @include flex;
    position: relative;
    color: $corporate-color;

    .label-text {
      @include label-text;
    }

    .required-field {
      @include milano-bold-20;
      position: absolute;
      top: -4px;
      right: -12px;
      color: $tertiary-persianRed-200;
      content: '*';
    }

    .label-icon {
      display: block;
      font-size: 15px;
      margin-bottom: 2px;
      cursor: pointer;
    }
  }

  ::v-deep .e-control-wrapper.e-input-group {
    @include textfield-styles;
    position: relative;
    margin-bottom: 0;
    color: $gray-01 !important;
    border-color: $neutral-grey-400 !important;

    .lf-mask-input {
      width: 100%;

      &::selection {
        background-color: $selection-color !important;
        color: $gray-01 !important;
      }

      &::placeholder {
        font-size: 12px;
        color: $gray-02;
      }
    }

    &.e-input-focus {
      @include borders;
      background-color: transparent;
      border: 1px solid $main-1000 !important;

      &::before,
      &::after {
        display: none;
      }
    }

    .e-clear-icon::before {
      font-family: $lf-icons-font;
      font-size: 12px;
      color: $main-1000;
      content: '\e938';
    }
  }

  &.disabled-field {
    pointer-events: none;

    .label-container .label-text {
      color: $disabled-color;

      &::after {
        color: $disabled-color;
      }
    }

    ::v-deep .e-input-group.e-control-wrapper {
      background-color: $gray-04;

      .lf-mask-input {
        color: $disabled-color;
      }

      .lf-mask-input::placeholder {
        color: $disabled-color;
      }

      &::after {
        color: $disabled-color;
      }

      &:hover {
        border-color: $error-color !important;
        background-color: $red-07;
      }
    }

    .help-message {
      color: $disabled-color;
    }
  }

  &.error-styles {
    ::v-deep .e-input-group.e-control-wrapper {
      border-color: $error-color !important;
      background-color: $red-07;

      .e-clear-icon::before {
        color: $error-color;
      }

      .lf-mask-input {
        color: $error-color;
      }

      .lf-mask-input::placeholder {
        font-size: 12px;
        color: $error-color;
      }

      &::after {
        color: $error-color;
      }

      &:hover {
        border-color: $error-color !important;
        background-color: $red-07;
      }
    }
  }
}
</style>
